import React, { useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";

/**
 * DateFormatControls
 * - Storage formats are FIXED.
 * - Display formats are configurable per field & per mode (date vs datetime).
 * - Resets state when switching fields.
 * - Uses date-fns tokens.
 */

type DateConfig = {
  displayFormatDate?: string;
  displayFormatDateTime?: string;
  storageFormatDate?: string;
  storageFormatDateTime?: string;
};

type FieldLike = {
  id: string;
  type?: string;
  showTimeSelect?: boolean;
  dateConfig?: DateConfig;
  defaultToToday?: boolean; 
  defaultValue?: string | null;  // optional: helps you clear conflicting defaults
};

interface DateFormatControlsProps {
  field: FieldLike;
  onPatch: (patch: Partial<FieldLike>) => void;
}

type Option = { label: string; value: string };

// ---- FIXED STORAGE FORMATS (single source of truth) ----
const STORAGE_FMT_DATE = "yyyy-MM-dd";
const STORAGE_FMT_DATETIME = "yyyy-MM-dd'T'HH:mm:ssXXX";

// ---- Presets for UX only (display formats) ----
const DATE_PRESETS = [
  "MM/dd/yyyy",
  "dd/MM/yyyy",
  "yyyy-MM-dd",
  "MMM dd yyyy",
  "dd MMM yyyy",
  "MMM dd, yyyy",
  "MMM d, yyyy",
  "MMMM d, yyyy",
  "MMMM dd, yyyy",
  "MMMM dd yyyy",
] as const;

const DATETIME_PRESETS = [
  "MM/dd/yyyy h:mm aa",
  "dd/MM/yyyy HH:mm",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd'T'HH:mm:ssXXX",
  "MMM dd yyyy h:mm aa",
  "MMMM dd, yyyy h:mm aa",
  "MMMM dd yyyy h:mm aa",
] as const;

const toOptions = (arr: readonly string[]): Option[] =>
  arr.map((s) => ({ label: s, value: s }));

const DateFormatControls: React.FC<DateFormatControlsProps> = ({
  field,
  onPatch,
}) => {
  const isDT = !!field.showTimeSelect;
  const dc = field.dateConfig || {};

  // Seed from DISPLAY (not storage), else sensible defaults
  const initialDisplay = isDT
    ? dc.displayFormatDateTime || "MMM dd yyyy h:mm aa"
    : dc.displayFormatDate || "MMM dd yyyy";

  const [displayFmt, setDisplayFmt] = useState<string>(initialDisplay);

  // Remount/reset when field id changes (switching between fields)
  useEffect(() => {
    const fresh = isDT
      ? field.dateConfig?.displayFormatDateTime || "MMM dd yyyy h:mm aa"
      : field.dateConfig?.displayFormatDate || "MMM dd yyyy";
    setDisplayFmt(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.id]);

  // Sync when flipping date <-> datetime
  useEffect(() => {
    const nextDisplay = isDT
      ? field.dateConfig?.displayFormatDateTime || "MMM dd yyyy h:mm aa"
      : field.dateConfig?.displayFormatDate || "MMM dd yyyy";
    setDisplayFmt(nextDisplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDT]);

  // Always write FIXED storage; only display is user-configurable
  useEffect(() => {
    if (isDT) {
      onPatch({
        showTimeSelect: true,
        dateConfig: {
          displayFormatDateTime: displayFmt,
          storageFormatDateTime: STORAGE_FMT_DATETIME,
          // keep date display around for quick flip-back
          displayFormatDate: field.dateConfig?.displayFormatDate,
          storageFormatDate: STORAGE_FMT_DATE,
        },
      });
    } else {
      onPatch({
        showTimeSelect: false,
        dateConfig: {
          displayFormatDate: displayFmt,
          storageFormatDate: STORAGE_FMT_DATE,
          // keep datetime display around for quick flip-forward
          displayFormatDateTime: field.dateConfig?.displayFormatDateTime,
          storageFormatDateTime: STORAGE_FMT_DATETIME,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDT, displayFmt]);

  const options = useMemo<Option[]>(
    () => toOptions(isDT ? DATETIME_PRESETS : DATE_PRESETS),
    [isDT]
  );

  const currentValue: Option | null = displayFmt
    ? { label: displayFmt, value: displayFmt }
    : null;

  // tiny heads-up if time tokens used in date-only mode (non-blocking)
  const hasTimeTokens = /H|h|m|s|a|A/.test(displayFmt);
  const showTimeWarning = !isDT && hasTimeTokens;

  return (
    <div style={{ borderTop: "1px solid #eee", paddingTop: 12, marginTop: 12 }}>
      <h6 className="mb-2">Date &amp; Time Formats</h6>

      <div className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id={`date-time-toggle-${field.id}`}
          checked={isDT}
          onChange={(e) => onPatch({ showTimeSelect: e.target.checked })}
        />
        <label
          className="form-check-label"
          htmlFor={`date-time-toggle-${field.id}`}
        >
          Enable time selection
        </label>
      </div>

      <div className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id={`preselect-today-${field.id}`}
          checked={!!field.defaultToToday}
          onChange={(e) => {
            const checked = e.target.checked;
            onPatch({
              defaultToToday: checked,
              defaultValue: checked ? undefined : field.defaultValue,
            });
          }}
        />
        <label
          className="form-check-label"
          htmlFor={`preselect-today-${field.id}`}
        >
          Preselect today’s date
        </label>
      </div>

      <label className="form-label">
        Format {isDT ? "(date-time)" : "(date)"}
      </label>
      <CreatableSelect<Option, false>
        isClearable={false}
        options={options}
        value={currentValue}
        onChange={(opt) => setDisplayFmt(opt?.value || "")}
        onCreateOption={(input) => setDisplayFmt(input)}
        placeholder="Pick or type a format"
        formatCreateLabel={(input) => `Use custom: ${input}`}
        className="custom-select"
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />

      {showTimeWarning && (
        <small className="text-warning d-block mt-2">
          Heads-up: time tokens will be ignored for a date-only field.
        </small>
      )}
    </div>
  );
};

export default DateFormatControls;
